import React, { useCallback, useRef, useState } from 'react';
import { ChevronDownIcon } from 'lucide-react';
import { useDismiss } from '../hooks/useDismiss';
import { DropdownOption, DropdownPanel } from './DropdownPanel';
type InlineSelectProps = {
  icon: React.ReactNode;
  label: string;
  value: string;
  options: string[];
  onChange: (value: string) => void;
  size?: 'md' | 'lg';
  placeholder?: string;
  invalid?: boolean;
  describedBy?: string;
  // Optional explanation shown at the top of the open list
  heading?: string;
  intro?: string;
  renderIcon?: (option: string) => React.ReactNode;
  iconWidth?: string;
  dividerBefore?: string; // option that starts a separated closing group
};

// Compact text-style dropdown for the request bar's top options row
export function InlineSelect({
  icon,
  label,
  value,
  options,
  onChange,
  size = 'md',
  placeholder,
  invalid,
  describedBy,
  heading,
  intro,
  renderIcon,
  iconWidth,
  dividerBefore
}: InlineSelectProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const close = useCallback(() => setOpen(false), []);
  useDismiss(rootRef, open, close);
  const empty = !value;
  return <div ref={rootRef} className="relative" data-id="element-584" data-mp="15xk5ynkdswnk">
      <button type="button" onClick={() => setOpen(o => !o)} aria-haspopup="listbox" aria-expanded={open} aria-label={`${label}: ${value || 'not chosen'}`} aria-invalid={invalid || undefined} aria-describedby={describedBy} className={`flex items-center gap-2 transition-opacity duration-150 hover:opacity-70 ${size === 'lg' ? 'text-[16px]' : 'text-[15px]'} ${empty ? 'font-medium text-ink/80' : 'font-medium'} ${invalid ? 'underline decoration-ink underline-offset-4' : ''}`} data-id="element-585" data-mp="15w96v11vsd2s">
        {icon}
        {value || placeholder}
        <ChevronDownIcon className={`h-4 w-4 transition-transform duration-300 ease-luxe ${open ? 'rotate-180' : ''}`} strokeWidth={1.5} aria-hidden="true" data-id="element-586" data-mp="2hav3pmpmzdoq" />
      </button>
      <DropdownPanel open={open} className={`left-0 ${heading ? 'w-[320px]' : 'w-[240px]'}`} data-id="element-587" data-mp="ffovvgk9t8v4">
        {heading && <div className="border-b border-line px-5 pb-4 pt-5" data-id="element-588" data-mp="rawjfm2c2jb">
            <p className="font-editorial text-[20px] leading-[24px]" data-id="element-589" data-mp="opv2rpofom3">{heading}</p>
            {intro && <p className="mt-1.5 text-[13px] font-light leading-[19px] text-ink/70" data-id="element-590" data-mp="5nsyy8tx7osh">{intro}</p>}
          </div>}
        <ul role="listbox" aria-label={label} className="max-h-[380px] overflow-y-auto py-2" data-id="element-591" data-mp="yejberrk6dcc">
          {options.map(o => <DropdownOption key={o} label={o} icon={renderIcon?.(o)} iconWidth={iconWidth} dense={Boolean(renderIcon)} divided={o === dividerBefore} selected={o === value} onSelect={() => {
          onChange(o);
          setOpen(false);
        }} data-id="element-592" data-mp="kfjobojp26ia" />)}
        </ul>
      </DropdownPanel>
    </div>;
}